import axios from 'axios';
import { CONFIGS, KEY_ACCESS_TOKEN, KEY_REFRESH_TOKEN } from './Constants/constant';
import { captureException } from '@sentry/react';
export let axiosInstance;

const BASE_URL = CONFIGS.BASE_URL;

export const getAxiosInstance = () => {
  axiosInstance = axios.create({
    baseURL: CONFIGS.BASE_URL,
    timeout: 25000,
  });

  axiosInstance.interceptors.request.use((req) => {
    req.headers.Authorization = `Bearer ${localStorage.getItem(KEY_ACCESS_TOKEN)}`;
    req.headers['access-token'] = localStorage.getItem(KEY_ACCESS_TOKEN);
    return req;
  });

  //Response interceptor for API calls
  axiosInstance.interceptors.response.use(
    (resp) => {
      return resp;
    },
    async function (error) {
      captureException(error);
      if (!error.response?.config && error.response?.status !== 401) {
        return error;
      }

      if (error.response?.status === 400 || error.response?.status === 404 || error.response?.status === 500) {
        return error;
      }

      if (localStorage.refresh_token && (error.response.status === 402 || error.response.status === 502 || error?.response?.data?.error_code == 'jwt_token_expired')) {
        const url = CONFIGS.REFRESH_URL;
        try {
          const res = await axios.post(url, {
            refreshToken: localStorage.refresh_token,
          });
          localStorage.setItem(KEY_ACCESS_TOKEN, res?.data?.accessToken?.token);
          localStorage.setItem(KEY_REFRESH_TOKEN, res?.data?.refreshToken?.token);
          error.response.config.headers['Authorization'] = `Bearer ${res?.data?.accessToken?.token}`;
          error.response.config.headers['access-token'] = localStorage.getItem(KEY_ACCESS_TOKEN);
          return axios(error.response.config);
        } catch (err) {
          localStorage.clear();
          window.location.reload();
        }
      } else {
        captureException(error);
        localStorage.clear();
        window.location.reload();
        return axios(error.response.config);
      }
    }
  );
};

// Checks Contact.Is_Active__c (RecordType = Nominee) for the given contactId.
// Called from AuthContext during session hydration, before axiosInstance is set up,
// so it uses a standalone axios call instead of axiosInstance.
export const checkNomineeActiveStatus = async (contactId) => {
  try {
    const response = await axios.get(`${BASE_URL}v1/application/nominee/active-status`, {
      params: { contactId },
      headers: {
        Authorization: `Bearer ${localStorage.getItem(KEY_ACCESS_TOKEN)}`,
        'access-token': localStorage.getItem(KEY_ACCESS_TOKEN),
      },
      timeout: 8000,
    });
    return response?.data || null;
  } catch (error) {
    console.log(error, 'checkNomineeActiveStatus error');
    return null;
  }
};

export const razorpayInitiate = async (data) => {
  const path = `/v1/razorpay/initiate`;
  const response = await axiosInstance.post(`${path}`, data);
  return response && response.data ? response.data : null;
};

export const verifyRazorpay = async (order_id, razorpay_payment_id, razorpay_signature, date) => {
  const path = `/v1/razorpay/verifyOrderSuccess?order_id=${order_id}&razorpay_payment_id=${razorpay_payment_id}&razorpay_signature=${razorpay_signature}`;
  const response = await axiosInstance.get(`${path}`);
  return response && response.data ? response.data : null;
};

export const getAllPrograms = async (applicantId) => {
  const path = `v1/forms/getAllForms`;
  const res = await axiosInstance.get(`${path}`);
  return res && res.data ? res.data : null;
};

export const getFormByFormId = async (formId) => {
  try {
    const path = `v1/forms/getFormsById?id=${formId}`;
    console.log('getFormByFormId API call:', path);
    const res = await axiosInstance.get(`${path}`);
    console.log('getFormByFormId API response:', res);
    
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: 'No data received' };
    }
  } catch (error) {
    console.error('getFormByFormId API error:', error);
    return { success: false, error: error.response?.data || error.message };
  }
};

export const getApplicationsByApplicantId = async (applicantid) => {
  const path = `/v1/application/getAllApplications?applicantid=${applicantid}`;
  const res = await axiosInstance.get(`${path}`);
  return res && res.data ? res.data : null;
};

export const getApplicationByApplicationId = async (applicationId) => {
  const path = `/v1/application/getApplication?applicationid=${applicationId}`;
  const res = await axiosInstance.get(`${path}`);
  return res && res.data ? res.data : null;
};

export const getApplicationAffiliation = async (applicationId) => {
  try {
    const path = `/v1/application/affiliation/${applicationId}`;
    const res = await axiosInstance.get(`${path}`);
    if (res && res.data) {
      // Backend returns { success: true, data: { affiliationId, application } }
      return res.data;
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    console.log(error, 'getApplicationAffiliation error');
    return { success: false, error: error.response?.data || error.message };
  }
};

export const createUpdateApplication = async (data) => {
  const path = `/v1/application/portal/save`;
  const res = await axiosInstance.post(`${path}`, data);
  return res;
};

export const updateApplicationStatus = async (applicationId, status, applicationSubmissionTime = null) => {
  const path = `/v1/application/status/${applicationId}`;
  const body = { status };
  if (applicationSubmissionTime) {
    body.Application_Submission_Time__c = applicationSubmissionTime;
  }
  const res = await axiosInstance.patch(`${path}`, body);
  return res;
};

export const deleteApplicationRecords = async (type, id) => {
  const path = `/v1/application/portal/delete?type=${type}&id=${id}`;
  const res = await axiosInstance.delete(`${path}`);
  return res && res.data ? res.data : null;
};

export const employmentRecords = async () => {
  const path = `/v1/application/employment/records`;
  const res = await axiosInstance.get(`${path}`);
  return res && res.data ? res.data : null;
};

export const contactRecords = async () => {
  const path = `/v1/application/contact/records`;
  const res = await axiosInstance.get(`${path}`);
  return res && res.data ? res.data : null;
};

export const getDocuments = async (applicantId) => {
  const path = `/v1/application/documents?applicantId=${applicantId}`;
  const res = await axiosInstance.get(`${path}`);
  return res && res.data ? res.data : null;
};

export const createDocuments = async (data) => {
  const path = '/v1/application/portal/save';
  const res = await axiosInstance.post(`${path}`, data);
  return res && res.data ? res.data : null;
};

export const getDocumentUrl = async (formData, appId) => {
  const path = `/v1/application/upload/${appId}/document`;
  const res = await axiosInstance.post(`${path}`, formData);
  return res && res.data ? res.data : null;
};

export const getOfferLetter = async (applicantId) => {
  const path = `/v1/application/get/offerLetter?applicantId=${applicantId}`;
  const res = await axiosInstance.get(`${path}`);
  return res && res.data ? res.data : null;
};

export const getOfferLetterData = async (applicationId) => {
  const path = `/v1/application/get/offerLetterData?applicationId=${applicationId}`;
  const res = await axiosInstance.get(`${path}`);
  return res && res.data ? res.data : null;
};

export const getFeeAssignments = async (applicantid) => {
  const path = `/v1/application/get/feeAssignments?applicantid=${applicantid}`;
  const res = await axiosInstance.get(`${path}`);
  return res && res.data ? res.data : null;
};

export const saveOfferLetterResponse = async (data) => {
  const path = `/v1/application/save/offerStatus`;
  const res = await axiosInstance.post(`${path}`, data);
  return res && res.data ? res.data : null;
};

export const getFeeDetails = async (applicationId) => {
  const path = `/v1/application/feeAssignmentId/${applicationId}`;
  const res = await axiosInstance.get(`${path}`);
  return res && res.data ? res.data : null;
};
// export const getDownlaodReceipt = async (receiptId) => {
//   const path = `/v1/application/downloadPaymentReceipt/${receiptId}`;
//   const res = await axiosInstance.get(`${path}`);
//   return res && res.data ? res.data : null;
// };
export const getDownlaodReceipt = async (receiptId) => {
  const path = `/v1/application/downloadPaymentReceipt/${receiptId}`;
  const res = await axiosInstance.get(path, {
    responseType: 'arraybuffer',
  });
  return res;
};

export const getApplicationPreviewPdf = async (applicationId) => {
  const path = `/v1/application/preview/pdf?applicantId=${applicationId}`;
  const res = await axiosInstance.get(`${path}`, {
    responseType: 'arraybuffer',
  });
  return res;
};

export const saveFeePayments = async (data) => {
  const path = '/v1/application/save/payment';
  const res = await axiosInstance.post(`${path}`, data);
  return res && res.data ? res.data : null;
};

export const postInitatePayment = async (data) => {
  const path = `payments/flywire/initiate`;
  const res = await axiosInstance.post(`${path}`, data);
  return res && res.data ? res.data : null;
};

export const getPaymentTransactions = async (applicantId) => {
  const path = `/v1/application/transactions?applicantId=${applicantId}`;
  const res = await axiosInstance.get(`${path}`);
  return res && res.data ? res.data : null;
};

export const createEMBAToken = async (id) => {
  const path = `/v1/application/createtoken/emba`;
  const res = await axiosInstance.get(`${path}?id=${id}`);
  return res && res.data ? res.data : null;
};

export const verifyEMBAToken = async (token, appId, expiry) => {
  const path = `/v1/application/verifytoken/emba?token=${encodeURIComponent(token)}&appId=${appId}&tokenExpiry=${expiry}`;
  const res = await axiosInstance.get(`${path}`);
  return res && res.data ? res.data : null;
};

export const updateLead = async (data) => {
  const path = `update/lead?contact=${data}`;
  const res = await axiosInstance.post(`${path}`);
  return res && res.data ? res.data : null;
};

// create lead
export const createLead = async (data) => {
  try {
    const path = `v1/lead/create`;
    const res = await axiosInstance.post(`${path}`, data);
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    console.log(error, 'error');
    return error;
  }
};

export const getLeadDetails = async (email) => {
  try {
    console.log(email, 'email');
    const path = '/v1/lead/get/details';
    const res = await axiosInstance.get(`${path}?email=${email}`);
    console.log(res, 'dataRes');
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    console.log(error, 'error');
    return error;
  }
};

// Verification Lead
export const verificationLead = async (token) => {
  try {
    const path = `v1/lead/verification?token=${token}`;
    const res = await axiosInstance.get(`${path}`);
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    console.log(error, 'error');
    return error;
  }
};

export const authenticateEmailPasswordLead = async (payload) => {
  const path = `v1/lead/authenticate`;
  const res = await axiosInstance.post(`${path}`, payload);
  return res && res.data ? res.data : null;
};

export const forgotPassword = async (email) => {
  const path = `v1/lead/forgot/password`;
  const res = await axiosInstance.post(`${path}?email=${email}`);
  return res && res.data ? res.data : null;
};

export const resendVerificationLink = async (email) => {
  const path = `v1/lead/resend/verification`;
  const res = await axiosInstance.post(`${path}?email=${email}`);
  return res && res.data ? res.data : null;
};

// Registration OTP Verification APIs
export const verifyRegistrationOTP = async (payload) => {
  try {
    // For now, simulate successful verification for testing
    // TODO: Replace with actual API call when backend is ready
    console.log('Mock OTP verification for payload:', payload);
    
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Mock success response
    return { 
      success: true, 
      data: { 
        message: 'OTP verified successfully',
        verified: true 
      } 
    };
    
    // Uncomment below when backend is ready:
    // const path = `v1/lead/verify/registration/otp`;
    // const res = await axiosInstance.post(`${path}`, payload);
    // if (res && res.data) {
    //   return { success: true, data: res.data };
    // } else {
    //   return { success: false, error: res?.response };
    // }
  } catch (error) {
    console.log(error, 'error');
    return { success: false, error: error.response };
  }
};

export const resendVerificationOTP = async (payload) => {
  try {
    // For now, simulate successful resend for testing
    // TODO: Replace with actual API call when backend is ready
    console.log('Mock OTP resend for payload:', payload);
    
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Mock success response
    return { 
      success: true, 
      data: { 
        message: 'OTP resent successfully' 
      } 
    };
    
    // Uncomment below when backend is ready:
    // const path = `v1/lead/resend/otp`;
    // const res = await axiosInstance.post(`${path}`, payload);
    // if (res && res.data) {
    //   return { success: true, data: res.data };
    // } else {
    //   return { success: false, error: res?.response };
    // }
  } catch (error) {
    console.log(error, 'error');
    return { success: false, error: error.response };
  }
};

export const resetPassword = async (payload) => {
  const path = `v1/lead/reset/password`;
  const res = await axiosInstance.post(`${path}`, payload);
  return res && res.data ? res.data : null;
};

// Send Registration OTP for Executive Education
export const sendRegistrationOTP = async (payload) => {
  try {
    const path = `v1/auth/send/otp`;
    const res = await axiosInstance.post(`${path}`, payload);
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    console.log(error, 'sendRegistrationOTP error');
    return { success: false, error: error.response };
  }
};

// Send Login OTP for existing users
export const sendLoginOTP = async (payload) => {
  try {
    const path = `v1/auth/send/login/otp`;
    const res = await axiosInstance.post(`${path}`, payload);
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    console.log(error, 'sendLoginOTP error');
    return { success: false, error: error.response };
  }
};

// Verify Registration OTP for Executive Education
export const verifyOTP = async (payload) => {
  try {
    const path = `v1/auth/otp/verify`;
    const res = await axiosInstance.post(`${path}`, payload);
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    console.log(error, 'verifyOTP error');
    return { success: false, error: error.response };
  }
};

// Edit Email or Phone during Registration OTP flow
export const editEmailOrPhone = async (payload) => {
  try {
    const path = `v1/auth/edit/emailOrPhone`;
    const res = await axiosInstance.post(`${path}`, payload);
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    console.log(error, 'editEmailOrPhone error');
    return { success: false, error: error.response };
  }
};

export const postcreateCase = async (data) => {
  const path = `/v1/query/create`;
  const res = await axiosInstance.post(`${path}`, data);
  return res && res.data ? res.data : null;
};
export const getAllQueryByApplicantId = async (applicantid) => {
  const path = `/v1/query/getAllQuery?applicantid=${applicantid}`;
  const res = await axiosInstance.get(`${path}`);
  return res && res.data ? res.data : null;
};
export const postopenNotification = async (data) => {
  try {
    const path = `v1/query/openNotification`;
    const res = await axiosInstance.post(`${path}`, data);
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    console.log(error, 'error');
    return error;
  }
};

export const getSlots = async (applicationFormId) => {
  try {
    const path = 'v1/interview/get/slots';
    const res = await axiosInstance.get(`${path}?applicationFormId=${applicationFormId}`);
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    return error;
  }
};

export const updateSlots = async (body) => {
  try {
    const path = 'v1/interview/update/interviewslot';
    const res = await axiosInstance.post(`${path}`, body);
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    return error;
  }
};

export const getStudentInterviewSlot = async (contactId) => {
  try {
    const path = 'v1/interview/get/student/slot';
    const res = await axiosInstance.get(`${path}?contactId=${contactId}`);
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    return error;
  }
};

// FAQ API function
export const getFaq = async () => {
  try {
    const path = `/v1/query/faq`;
    const res = await axiosInstance.get(path);
    if (res && res.data) {
      // Transform Salesforce SOQL response format to match expected format
      const records = res?.data?.records || [];
      const transformedData = records.map((record) => ({
        id: record.Id,
        question: (record.Question__c || '').trim(),
        answer: (record.Answer__c || '').replace(/\r\n/g, '\n').trim(),
        category: (record.FAQ_Type__c || 'General').trim(),
        isFrequent: false // This field may not exist in Salesforce, defaulting to false
      }));
      return { success: true, data: transformedData };
    } else {
      return { success: false, error: 'No data received' };
    }
  } catch (error) {
    console.log(error, 'getFaq error');
    return { success: false, error: error.response?.data || error.message };
  }
};

// Update Case Feedback API function
export const updateCaseFeedback = async (caseId, feedback, rating) => {
  try {
    const path = `/v1/query/case/${caseId}/feedback`;
    const res = await axiosInstance.patch(path, { 
      feedback: feedback || '',
      rating: rating || 0 
    });
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    console.log(error, 'updateCaseFeedback error');
    return { success: false, error: error.response?.data || error.message };
  }
};

// Get Case Comments API function
export const getCaseComments = async (caseId) => {
  try {
    const path = `/v1/query/case/comments?parentId=${caseId}`;
    const res = await axiosInstance.get(path);
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    console.log(error, 'getCaseComments error');
    return { success: false, error: error.response?.data || error.message };
  }
};

// Create Case Comment API function
export const createCaseComment = async (caseId, commentBody, file, options = {}) => {
  try {
    const path = `/v1/query/case/comment`;
    const formData = new FormData();
    formData.append('caseId', caseId);

    if (commentBody !== undefined && commentBody !== null) {
      formData.append('commentBody', commentBody);
    }

    const isPublished =
      options.isPublished !== undefined ? options.isPublished : true;
    formData.append('isPublished', isPublished);

    if (file) {
      formData.append('file', file);
    }

    const res = await axiosInstance.post(path, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return res.data;
  } catch (error) {
    console.log(error, 'createCaseComment error');
    throw error;
  }
};

// Ticket History API function
export const getTicketHistory = async (contactId) => {
  try {
    const path = `/v1/query/ticketHistory?contactId=${contactId}`;
    const res = await axiosInstance.get(path);
    if (res && res.data) {
      // Transform Salesforce response to match expected format
      const records = res.data?.records || [];
      return { 
        success: true, 
        data: {
          records: records,
          totalSize: res.data?.totalSize || records.length
        }
      };
    } else {
      return { success: false, error: 'No data received' };
    }
  } catch (error) {
    console.log(error, 'getTicketHistory error');
    return { 
      success: false, 
      error: error.response?.data || error.message 
    };
  }
};

// Services Offering API function
export const getServicesOffering = async () => {
  try {
    const res = await axiosInstance.get('/api/services');
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { 
        success: true, 
        data: [
          { id: 1, name: 'Technical Support' },
          { id: 2, name: 'Application Support' },
          { id: 3, name: 'General Inquiry' }
        ]
      };
    }
  } catch (error) {
    return { 
      success: true, 
      data: [
        { id: 1, name: 'Technical Support' },
        { id: 2, name: 'Application Support' },
        { id: 3, name: 'General Inquiry' }
      ]
    };
  }
};

// Raise Complaint API function
export const raiseComplaint = async (payload) => {
  try {
    const res = await axiosInstance.post('/api/complaints', payload);
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: true, message: 'Complaint submitted successfully' };
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Fee Management APIs

// Get Fee Assignments by Affiliation ID
export const getFeeAssignmentsByAffiliation = async (affiliationId) => {
  try {
    const path = `/v1/fee/feeAssignments?affiliationId=${affiliationId}`;
    const res = await axiosInstance.get(`${path}`);
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    console.log(error, 'getFeeAssignmentsByAffiliation error');
    return { success: false, error: error.response };
  }
};

// Apply Promo Code
export const applyPromoCode = async (payload) => {
  try {
    const path = `/v1/fee/applyPromoCode`;
    const res = await axiosInstance.post(`${path}`, payload);
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    console.log(error, 'applyPromoCode error');
    return { success: false, error: error.response };
  }
};

// Remove Applied Discount (Deactivate Promo Code)
export const removeAppliedDiscount = async (appliedDiscountId) => {
  try {
    const path = `/v1/fee/removeAppliedDiscount/${appliedDiscountId}`;
    const res = await axiosInstance.patch(`${path}`);
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    console.log(error, 'removeAppliedDiscount error');
    return { success: false, error: error.response };
  }
};

export const applyInstallmentPromoCode = async (payload) => {
  try {
    const path = `/v1/fee/applyInstallmentPromoCode`;
    const res = await axiosInstance.post(`${path}`, payload);
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    console.log(error, 'applyInstallmentPromoCode error');
    return { success: false, error: error.response };
  }
};

export const removeInstallmentAppliedDiscount = async (appliedDiscountId) => {
  try {
    const path = `/v1/fee/removeInstallmentAppliedDiscount/${appliedDiscountId}`;
    const res = await axiosInstance.patch(`${path}`);
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    console.log(error, 'removeInstallmentAppliedDiscount error');
    return { success: false, error: error.response };
  }
};

// Get Fee Installments by Contact ID
export const getFeeInstallmentsByContact = async (contactId) => {
  try {
    const path = `/v1/fee/feeInstallments?contactId=${contactId}`;
    const res = await axiosInstance.get(`${path}`);
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    console.log(error, 'getFeeInstallmentsByContact error');
    return { success: false, error: error.response };
  }
};

// Save Payment Transaction
export const savePaymentTransaction = async (payload) => {
  try {
    const path = `/v1/fee/paymentTransaction`;
    const res = await axiosInstance.post(`${path}`, payload);
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    console.log(error, 'savePaymentTransaction error');
    return { success: false, error: error.response };
  }
};

// Bulk Payment Transaction
export const bulkPaymentTransaction = async (payload) => {
  try {
    const path = `/v1/fee/bulkPaymentTransaction`;
    const res = await axiosInstance.post(`${path}`, payload);
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    console.log(error, 'bulkPaymentTransaction error');
    return { success: false, error: error.response };
  }
};

export const programPlans = async (payload) => {
  try {
    const path = `/v1/application/programplans`;
    const res = await axiosInstance.get(`${path}`);
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    console.log(error, 'savePaymentTransaction error');
    return { success: false, error: error.response };
  }
};

export const customPrograms = async (accountId) => {
  try {
    const path = `/v1/application/custom/programs?accountId=${accountId}`;
    const res = await axiosInstance.get(`${path}`);
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    console.log(error, 'Get Custom Programs error');
    return { success: false, error: error.response };
  }
};

export const individualDashboardRetrieve = async (accountId) => {
  try {
    const path = `/v1/application/individual/dashboard/retrieve/accountId/${accountId}`;
    const res = await axiosInstance.get(`${path}`);
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    console.log(error, 'savePaymentTransaction error');
    return { success: false, error: error.response };
  }
};

export const profileApplication = async (accountId) => {
  try {
    const path = `/v1/application/profile/information?contactId=${accountId}`;
    const res = await axiosInstance.get(`${path}`);
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    return { success: false, error: error.response };
  }
}

export const updateProfileApplication = async (payload) => {
  try {
    const path = `/v1/application/update/profile/information`;
    const res = await axiosInstance.post(`${path}`, payload);
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    console.log(error, 'updateProfileApplication error');
    return { success: false, error: error.response };
  }
}

export const dashboardRetrieve = async (accountId) => {
  try {
    const path = `/v1/application/dashboard/retrieve/accountId/${accountId}`;
    const res = await axiosInstance.get(`${path}`);
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    return { success: false, error: error.response };
  }
};

export const feeInstallment = async (contactId) => {
  try {
    const path = `/v1/fee/feeInstallments?contactId=${contactId}`;
    const res = await axiosInstance.get(`${path}`);
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    return { success: false, error: error.response };
  }
};

export const getAssignedCourses = async (contactId) => {
  try {
    const path = `/v1/application/assignedCourses?contactId=${contactId}`;
    const res = await axiosInstance.get(`${path}`);
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    return { success: false, error: error.response };
  }
};

// Get Promocode Details
export const getPromocodeDetails = async (programVersionId, date) => {
  try {
    const path = `/v1/fee/promocode/details?programVersionId=${programVersionId}&date=${date}`;
    const res = await axiosInstance.get(`${path}`);
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    console.log(error, 'getPromocodeDetails error');
    return { success: false, error: error.response };
  }
};

// Nomination Management APIs

// Get nominee contact list
export const getNomineeContactList = async (accountId, programVersionId) => {
  try {
    const path = `/v1/application/account/contact?accountId=${accountId}&programVersionId=${programVersionId}`;
    const res = await axiosInstance.get(`${path}`);
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    console.log(error, 'getNomineeContactList error');
    return { success: false, error: error.response };
  }
};

// Create contact after selecting nomination
export const createContactNomination = async (payload) => {
  try {
    const path = `/v1/application/create/contact/nomination`;
    const res = await axiosInstance.post(`${path}`, payload);
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    console.log(error, 'createContactNomination error');
    return { success: false, error: error.response };
  }
};

// Create nominee with nomination and contacts
export const createNominee = async (payload) => {
  try {
    const path = `/v1/application/create/nominee`;
    const res = await axiosInstance.post(`${path}`, payload);
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    console.log(error, 'createNominee error');
    return { success: false, error: error.response };
  }
};

// Get nomination data
export const getNominations = async (id) => {
  try {
    const path = `/v1/application/get/nominations/${id}`;
    const res = await axiosInstance.get(`${path}`);
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    console.log(error, 'getNominations error');
    return { success: false, error: error.response };
  }
};

// Get nominee pending actions
export const getNomineePendingActions = async (accountId) => {
  try {
    const path = `/v1/application/nominee/pending-actions?accountId=${accountId}`;
    const res = await axiosInstance.get(`${path}`);
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    console.log(error, 'getNomineePendingActions error');
    return { success: false, error: error.response };
  }
};

// Delete nominee(s)
export const deleteNominee = async (nominationId, contactIds) => {
  try {
    const contactIdsString = Array.isArray(contactIds) ? contactIds.join(',') : contactIds;
    const path = `/v1/application/delete/nominee?nominationId=${nominationId}&contactIds=${contactIdsString}`;
    const res = await axiosInstance.delete(`${path}`);
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    console.log(error, 'deleteNominee error');
    return { success: false, error: error.response };
  }
};

// Send nomination email
export const sendNominatedEmail = async (payload) => {
  try {
    const path = `/v1/application/send/nominated/email`;
    const res = await axiosInstance.post(`${path}`, payload);
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    console.log(error, 'sendNominatedEmail error');
    return { success: false, error: error.response };
  }
};

// Organization Management APIs

// GET - Retrieve Organization Details
export const getOrganizationDetails = async (accountId) => {
  try {
    const path = `/v1/organization/${accountId}`;
    const res = await axiosInstance.get(`${path}`);
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    console.log(error, 'getOrganizationDetails error');
    return { success: false, error: error.response };
  }
};

// PUT - Add New SPOC Contact
export const addSpocContact = async (payload) => {
  try {
    const path = `/v1/organization/addspoc`;
    const res = await axiosInstance.put(`${path}`, payload);
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    console.log(error, 'addSpocContact error');
    return { success: false, error: error.response };
  }
};

// PATCH - Make Existing Contact as Primary
export const makePrimaryContact = async (payload) => {
  try {
    const path = `/v1/organization/makeprimary`;
    const res = await axiosInstance.patch(`${path}`, payload);
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    console.log(error, 'makePrimaryContact error');
    return { success: false, error: error.response };
  }
};

// POST - Update Organization and Contacts
export const updateOrganization = async (payload) => {
  try {
    const path = `/v1/organization/`;
    const res = await axiosInstance.post(`${path}`, payload);
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    console.log(error, 'updateOrganization error');
    return { success: false, error: error.response };
  }
};

// DELETE - Delete SPOC Contact
export const deleteSpocContact = async (contactId) => {
  try {
    const path = `/v1/organization/contact/${contactId}`;
    const res = await axiosInstance.delete(`${path}`);
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    console.log(error, 'deleteSpocContact error');
    return { success: false, error: error.response };
  }
};

// Transaction History API
export const getTransactionHistory = async (contactId) => {
  try {
    const path = `/v1/application/transaction/history?contactId=${contactId}`;
    const res = await axiosInstance.get(`${path}`);
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    console.log(error, 'getTransactionHistory error');
    return { success: false, error: error.response };
  }
};

// Get Pro-Forma Invoices API
export const getProFormaInvoices = async (contactId) => {
  try {
    const path = `/v1/fee/proforma-invoices?contactId=${contactId}`;
    const res = await axiosInstance.get(`${path}`);
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    console.log(error, 'getProFormaInvoices error');
    return { success: false, error: error.response };
  }
};

export const updateFeeInstallmentRemarks = async (feeInstallmentId, remarks) => {
  try {
    const path = `/v1/fee/feeInstallment/${feeInstallmentId}/remarks`;
    const res = await axiosInstance.patch(`${path}`, { remarks });
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    console.log(error, 'updateFeeInstallmentRemarks error');
    return { success: false, error: error.response?.data || error.message };
  }
};

export const downloadApplication = async (applicationId) => {
  try {
    const path = `/v1/application/download/${applicationId}`;
    const res = await axiosInstance.get(`${path}`);
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    return { success: false, error: error.response };
  }
};

// Create Service Request Case
export const createServiceRequestCase = async (payload) => {
  try {
    const path = `/v1/query/serviceRequestCase`;
    const res = await axiosInstance.post(`${path}`, payload);
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    console.log(error, 'createServiceRequestCase error');
    return { success: false, error: error.response?.data || error.message };
  }
};

// Update Case Status (for cancellation)
export const updateCaseStatus = async (caseId, status) => {
  try {
    const path = `/v1/query/case/${caseId}/status`;
    const res = await axiosInstance.patch(`${path}`, { status });
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    console.log(error, 'updateCaseStatus error');
    return { success: false, error: error.response?.data || error.message };
  }
};

// Post Offline Payment Records
export const saveOfflinePaymentRecord = async (payload) => {
  try {
    const path = `/v1/application/offline/payment`;
    const res = await axiosInstance.post(`${path}`, payload);
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    console.log(error, 'saveofflinePaymentRecorderror');
    return { success: false, error: error.response?.data || error.message };
  }
};

// --- ExEd Learn portal SSO handoff ---------------------------------------

// Decides whether the "Learn" side-nav item is rendered at all.
export const getLearnEligibility = async () => {
  try {
    const res = await axiosInstance.get(`v1/sso/learn/eligibility`);
    return res && res.data ? res.data : null;
  } catch (error) {
    console.log(error, 'getLearnEligibility error');
    return null;
  }
};

// Mints the short-lived ticket the Learn portal exchanges for its own session.
export const getLearnSsoTicket = async () => {
  const res = await axiosInstance.post(`v1/sso/learn/ticket`);
  return res && res.data ? res.data : null;
};
